import clsx from 'clsx';
import Button, {Kind} from 'components/Button';
import Icon from 'components/Icon';
import {IconType} from 'components/Icon/types';
import {FC, MouseEventHandler, ReactNode, useEffect, useRef} from 'react';

import {alert, danger, success, system} from './styles.module.css';

export type NotificationButton =
    | {
          icon?: IconType;
          kind?: Kind;
          label: string;
      }
    | string;

export type NotificationProps = {
    button?: NotificationButton;
    children: ReactNode;
    className?: string;
    icon?: IconType;
    onClick?: MouseEventHandler;
    scrollIntoView?: boolean | ScrollIntoViewOptions;
    size?: 'base' | 'sm';
    title?: string;
    type?: NotificationType;
};

export type NotificationType = 'alert' | 'danger' | 'success' | 'system';

const TYPES: Record<NotificationType, string> = {
    alert,
    danger,
    success,
    system,
};

const Notification: FC<NotificationProps> = ({
    button,
    children,
    className,
    icon,
    onClick,
    scrollIntoView,
    size = 'base',
    title,
    type = 'system',
}) => {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (scrollIntoView === true) {
            ref.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
        } else if (scrollIntoView) {
            ref.current?.scrollIntoView(scrollIntoView);
        }
    }, [ref, scrollIntoView]);
    const safeButton: {icon?: IconType; kind?: Kind; label?: string} = button
        ? typeof button === 'string'
            ? {kind: 'secondary', label: button}
            : {icon: button.icon, kind: button.kind, label: button.label}
        : {icon: 'close', kind: 'tertiary'};

    return (
        <div
            className={clsx(
                'flex items-center rounded border',
                TYPES[type],
                size === 'base' ? 'px-4 py-2.5' : 'px-3 py-0.5',
                className,
            )}
            ref={ref}
            role="alert"
        >
            {icon && (
                <Icon
                    className={clsx('flex-none', title ? 'mr-2' : 'mr-3')}
                    icon={icon}
                    size="base"
                />
            )}
            <div
                className={clsx(
                    'flex-1 whitespace-pre-wrap text-grey-900',
                    size === 'base' ? 'text-base' : 'text-sm',
                )}
            >
                {title && <strong>{title}&nbsp;</strong>}
                {children}
            </div>
            {onClick && (
                <Button
                    className="ml-3 flex-none"
                    icon={safeButton.icon}
                    kind={safeButton.kind}
                    onClick={onClick}
                    size="xs"
                >
                    {safeButton.label}
                </Button>
            )}
        </div>
    );
};

export default Notification;
